/**
 * useITAccess — IT-Access + Presets für Mitarbeiterformular.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { employeesAPI, itAccessPresetsAPI } from '../../lib/api';

export function useITAccess({ employeeId, isEdit }) {
  const [itAccesses, setItAccesses] = useState([]);
  const [itPresets, setItPresets] = useState([]);
  const [loadingIT, setLoadingIT] = useState(false);
  const [newITAccess, setNewITAccess] = useState({ program: '', login: '', password: '', info: '' });
  const [editingITAccess, setEditingITAccess] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [newPreset, setNewPreset] = useState({ name: '', program: '', default_login_template: '', info: '', url: '', color: '#6B7280' });
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);

  const loadITData = useCallback(async () => {
    if (!employeeId) return;
    setLoadingIT(true);
    try {
      const [accessRes, presetsRes] = await Promise.all([
        employeesAPI.getITAccess(employeeId),
        itAccessPresetsAPI.getAll(),
      ]);
      setItAccesses(accessRes.data || []);
      setItPresets(presetsRes.data || []);
    } catch (error) {
      console.error('Failed to load IT data:', error);
    } finally {
      setLoadingIT(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (isEdit) loadITData();
  }, [isEdit, loadITData]);

  const handleAddITAccess = async () => {
    if (!newITAccess.program.trim()) {
      toast.error('Programm ist erforderlich');
      return;
    }
    try {
      await employeesAPI.addITAccess(employeeId, { employee_id: employeeId, ...newITAccess });
      toast.success('IT-Zugang hinzugefügt');
      setNewITAccess({ program: '', login: '', password: '', info: '' });
      loadITData();
    } catch {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleUpdateITAccess = async () => {
    if (!editingITAccess) return;
    try {
      await employeesAPI.updateITAccess(employeeId, editingITAccess.id, { employee_id: employeeId, ...editingITAccess });
      toast.success('IT-Zugang aktualisiert — Mitarbeiter wurde benachrichtigt');
      setEditingITAccess(null);
      loadITData();
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteITAccess = async (accessId) => {
    if (!window.confirm('IT-Zugang wirklich löschen?')) return;
    try {
      await employeesAPI.deleteITAccess(employeeId, accessId);
      toast.success('IT-Zugang gelöscht');
      loadITData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleAddFromPreset = async (presetId) => {
    try {
      await employeesAPI.addITAccessFromPreset(employeeId, presetId);
      toast.success('IT-Zugang aus Vorlage erstellt');
      loadITData();
    } catch {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleSavePreset = async () => {
    if (!newPreset.name.trim() || !newPreset.program.trim()) {
      toast.error('Name und Programm sind erforderlich');
      return;
    }
    try {
      if (editingPreset) {
        await itAccessPresetsAPI.update(editingPreset.id, newPreset);
        toast.success('Vorlage aktualisiert');
      } else {
        await itAccessPresetsAPI.create(newPreset);
        toast.success('Vorlage erstellt');
      }
      setPresetDialogOpen(false);
      setNewPreset({ name: '', program: '', default_login_template: '', info: '', url: '', color: '#6B7280' });
      setEditingPreset(null);
      loadITData();
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeletePreset = async (presetId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try {
      await itAccessPresetsAPI.delete(presetId);
      toast.success('Vorlage gelöscht');
      loadITData();
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  return {
    itAccesses, itPresets, loadingIT,
    newITAccess, setNewITAccess,
    editingITAccess, setEditingITAccess,
    showPasswords, setShowPasswords,
    newPreset, setNewPreset,
    presetDialogOpen, setPresetDialogOpen,
    editingPreset, setEditingPreset,
    handleAddITAccess, handleUpdateITAccess, handleDeleteITAccess,
    handleAddFromPreset, handleSavePreset, handleDeletePreset,
  };
}
